import {debug} from '@wix/document-manager-core'
import {
    type CreateTransactionRequest,
    type CreateTransactionResponse,
    type GetDocumentRequest,
    type GetDocumentResponse,
    type GetTransactionResponse,
    type HttpServer,
    type FragmentServer,
    type SchemaCollectionServer,
    type AppsRuntimeDataServer,
    type GetSchemaCollectionsByAppIdsRequest,
    type VersionedSchemaCollectionId,
    type TransformedAppRuntimeData,
    mapGetComponentsResponse
} from '@wix/document-manager-extensions'
import {
    type GetAppsRuntimeDataRequest,
    ComponentType,
    ComponentsOverridesType,
    RequestedFields
    // eslint-disable-next-line wix-editor/no-internal-import
} from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types'
import type {
    ContinuousSaveServer,
    SaveTransactionResponse
} from '@wix/document-manager-extensions/src/extensions/csave/csaveTypes'
import type {ViewsRESTRes, ViewsServer} from '@wix/document-manager-extensions/src/extensions/views/viewsTypes'
import type {
    CreateRevisionRequest,
    ServerCreateRevisionResponse,
    GetTransactionsResponse,
    SaveRequest
} from '@wix/document-manager-extensions/src/types'
import type {
    DistributePayload,
    DistributeRequest,
    FragmentTypesFilter,
    Metadata
} from '@wix/ambassador-document-management-document-store-v1-transaction/types'
import type {
    ListFragmentsMetadataResponse,
    GetFragmentResponse,
    FragmentTypeAndName
} from '@wix/ambassador-document-management-fragments-v1-fragment-snapshot/types'
import type {ModifySiteAppsResponse} from '@wix/ambassador-wix-html-editor-webapp/types'
import {
    getReportableFromError,
    HttpRequestError,
    Method,
    ReportableError,
    retryTaskAndReport
} from '@wix/document-manager-utils'
import type {AdapterLogger} from '../adapter/adapterLogger'
import {type CustomParser, fetchJsonWithAuthorization, Query, toQueryString} from '../utils/fetch'
import {AuthorizationStatusMap, InvalidAuthorizationMap} from './AuthorizationMap'
import {fetchClientSpecMap, META_SITE_APP_ID} from './clientSpecMap'
import {MAX_LONG, ServerFacade, getFragmentTypesFilter} from './serverFacade'
import type {Experiment, UserInfo} from '@wix/document-services-types'
import _ from 'lodash'
import {mapTransactionResponse, mapTransactionsResponse} from './dtos'
import type {SchemaCollection} from '@wix/ambassador-dm-schema-collection-v1-schema-collection/types'

const log = debug('csave')
const VERSIONED_SCHEMA_BATCH_SIZE = 100
const getInstanceHint = (instance: string) => instance?.slice(0, 4) || 'missing'

const EDITOR_PARASTORAGE_PREFIX = 'https://editor.parastorage.com/sites/'
const getStaticCssFileRequestUrl = (fileName: string) => `${EDITOR_PARASTORAGE_PREFIX}${fileName}.z?v=3`

interface FragmentsRejectionReason {
    issueSeverity: string
    errorType: string
    extras: {
        status: number
        statusText: string
        redirected: boolean
        type: string
        url: string
        requestId: string
        xSeenBy: string
        result: string
    }
    status: number
    details?: {
        applicationError?: {
            code: string
        }
    }
}

interface FastUploadRequest {
    staticFile: {
        mediaSource: string
        data: string
        siteId: string
    }
}

interface FastUploadResponse {
    name: string
}

interface GetStaticCssFileByNameResponse {
    message: string
    requestId: string
}

/**
 * based on
 * https://github.com/wix-private/editor-server/blob/master/wix-html-server/editor-document-store/src/main/proto/com/wixpress/editor/api/document_store.proto
 */

const getMetadata = (request: CreateTransactionRequest): Metadata => {
    return (
        ('metadata' in request && request.metadata) ||
        ('atomicTransaction' in request &&
            (request.atomicTransaction?.transaction?.metadata ||
                request.atomicTransaction?.transactionGroup?.transactions?.[0].metadata)) ||
        {}
    )
}

const parseText: CustomParser = async (response: Response, logger?: AdapterLogger) => {
    let result
    try {
        result = {message: await response.text()}
    } catch (error: any) {
        logger?.captureError(
            new ReportableError({
                errorType: 'fetchJsonWithAuthorization',
                message: error.message,
                tags: {op: 'parseResponse'},
                extras: {originalError: JSON.stringify(error)}
            })
        )
        result = {message: 'Unable to parse response as text'}
    }
    return result
}

export class ServerFacadeWithAuthorization
    extends ServerFacade
    implements
        ContinuousSaveServer,
        ViewsServer,
        FragmentServer,
        HttpServer,
        SchemaCollectionServer,
        AppsRuntimeDataServer
{
    private readonly fetchImpl: typeof fetch

    constructor(
        editorRootUrl: string,
        private logger: AdapterLogger,
        private experimentInstance: Experiment,
        private userInfo: UserInfo,
        private authorizationStatusMap: AuthorizationStatusMap = new InvalidAuthorizationMap(),
        fetchFn?: typeof fetch
    ) {
        super(editorRootUrl)
        this.fetchImpl = fetchFn ?? ((...args: Parameters<typeof fetch>) => globalThis.fetch(...args))
    }

    async deleteTransactions(instance: string, to: string = MAX_LONG): Promise<void> {
        // deleteTransaction must include body param to => the transactions up until this ID will be deleted
        const payload = {to}
        const result = await this.deleteJson(this.urls.transactions, payload)
        this.lastTransactionId = result.transactionId
        log.info('deleteTransactions done', result)
    }

    async createRevision(req: CreateRevisionRequest): Promise<ServerCreateRevisionResponse> {
        log.info('createRevision')
        return await this.post(this.urls.createRevision, req)
    }

    async getTransactions(
        afterTransactionId: string,
        untilTransactionId: string,
        branchId: string,
        fragmentTypesFilter?: FragmentTypesFilter
    ): Promise<GetTransactionsResponse> {
        const fragmentQuery = getFragmentTypesFilter(fragmentTypesFilter)
        const queryParams = {afterTransactionId, untilTransactionId, branchId, ...fragmentQuery}
        const res = await this.getJsonWithParams(this.urls.transactions, queryParams)
        return fragmentTypesFilter?.allTypes ? mapTransactionsResponse(res) : res
    }

    async getViews(): Promise<ViewsRESTRes> {
        return await this.getJsonWithParams(this.urls.views, {})
    }

    async distributeMessage(payloads: DistributePayload[], branchId?: string): Promise<void> {
        const distributeRequest: DistributeRequest = {
            payloads,
            ...(branchId ? {branchId} : {})
        }
        return await this.post(this.urls.distributor, distributeRequest)
    }

    async getTransaction(
        transactionId: string,
        branchId?: string,
        fragmentTypesFilter?: FragmentTypesFilter
    ): Promise<GetTransactionResponse> {
        const fragmentQuery = getFragmentTypesFilter(fragmentTypesFilter)
        const queryParams = {branchId, ...fragmentQuery}
        const res = await this.getJsonWithParams(`${this.urls.transactions}/${transactionId}`, queryParams ?? {})
        return fragmentTypesFilter?.allTypes ? mapTransactionResponse(res) : res
    }

    async getStore(
        branchId: string,
        afterTransactionId: string,
        untilTransactionId?: string,
        fragmentTypesFilter?: FragmentTypesFilter
    ): Promise<GetDocumentResponse> {
        const params: GetDocumentRequest = {afterTransactionId, untilTransactionId, branchId, fragmentTypesFilter}
        const authBeforeRequest = this.authorizationStatusMap.get(META_SITE_APP_ID)
        try {
            return await retryTaskAndReport({
                task: () => this.getJsonWithParams(this.urls.document, params as Query),
                checkIfShouldRetry: (e: any) => {
                    if (e?.status === 503) {
                        return {shouldRetry: true, reason: '503'}
                    }
                    return {shouldRetry: false}
                },
                interactionName: 'retry_get_store',
                maxRetries: 1,
                logger: this.logger
            })
        } catch (e: any) {
            const authAfterRequest = this.authorizationStatusMap.get(META_SITE_APP_ID)
            this.logger.captureError(
                getReportableFromError(e, {
                    errorType: 'getDocumentFailed',
                    message: 'getDocument failed',
                    tags: {op: 'ServerFacadeWithAuthorization.getStore'},
                    extras: {authBeforeRequest, authAfterRequest}
                })
            )
            throw e
        }
    }

    /**
     * first createTransaction should be sent without lastTransactionId, the subsequent calls must include the previous tx id as lastTransactionId
     * PendingServerTransaction
     */
    async save(payload: CreateTransactionRequest): Promise<CreateTransactionResponse> {
        log.info('sending tx')
        const {correlationId} = getMetadata(payload) || {}
        this.logger.interactionStarted('save', {
            extras: {correlationId}
        })
        const response = await this.post<CreateTransactionResponse>(this.urls.save, payload)
        this.logger.interactionEnded('save', {
            extras: {correlationId, requestId: response?.requestId}
        })
        return response
    }

    async asyncSave(payload: SaveRequest): Promise<void> {
        log.info('sending pending tx')
        const correlationIds = payload.transactions?.map(tx => tx.correlationId)
        this.logger.interactionStarted('asyncSave', {
            extras: {correlationIds}
        })
        const response: any = await this.post<void>(this.urls.save2, payload)
        this.logger.interactionEnded('asyncSave', {
            extras: {correlationIds, requestId: response?.requestId}
        })
        return response
    }

    async post<T = any, R = any>(
        url: string,
        data?: R,
        headers?: Record<string, string>,
        signal?: AbortSignal
    ): Promise<T> {
        return this.fetch<T>(url, 'POST', data, headers, undefined, signal)
    }

    async deleteJson<T = any, R = any>(url: string, data?: R, headers?: Record<string, string>): Promise<T> {
        return this.fetch<T>(url, 'DELETE', data, headers)
    }

    async refreshInstanceIfExpired(appId: string, source: string): Promise<AuthorizationStatusMap> {
        const auth = this.authorizationStatusMap.get(appId)
        if (auth.isExpired()) {
            const interactionName = 'authorizationRefresh'
            log.info('authorization instance is stale')
            const eventGuid = _.random(10000, true)
            this.logger.interactionStarted(interactionName, {
                eventGuid,
                tags: {source},
                extras: {
                    expiration: auth?.expirationDate,
                    hint: getInstanceHint(auth?.instance)
                }
            })
            await this.refreshClientSpecMap()
            const updatedAuth = this.authorizationStatusMap.get(appId)
            this.logger.interactionEnded(interactionName, {
                eventGuid,
                tags: {source},
                extras: {
                    expiration: updatedAuth?.expirationDate,
                    hint: getInstanceHint(updatedAuth?.instance)
                }
            })
        }
        return this.authorizationStatusMap
    }

    async refreshClientSpecMap() {
        this.logger.interactionStarted('refreshClientSpecMap')
        this.authorizationStatusMap = await fetchClientSpecMap(
            this.fetchImpl,
            this.authorizationStatusMap.get(META_SITE_APP_ID).metaSiteId,
            this.experimentInstance,
            this.logger
        )
        this.logger.interactionEnded('refreshClientSpecMap')
    }

    async fetch<T = any, R = any>(
        url: string,
        method: Method,
        data?: R,
        headers?: Record<string, string>,
        customParser?: CustomParser,
        signal?: AbortSignal
    ): Promise<T> {
        try {
            await this.refreshInstanceIfExpired(META_SITE_APP_ID, 'fetchWithAuthorization')
        } catch (e: any) {
            if (e instanceof HttpRequestError && [404, 403].includes(e.status)) {
                throw e
            }
            this.logger.captureError(
                getReportableFromError(e, {
                    errorType: 'fetchAuthRefreshError',
                    message: 'fetch failed',
                    tags: {op: 'ServerFacadeWithAuthorization.fetch'}
                })
            )
        }
        return fetchJsonWithAuthorization(
            url,
            this.authorizationStatusMap.getInstance(META_SITE_APP_ID),
            method,
            data,
            this.logger,
            this.experimentInstance,
            this.userInfo,
            headers,
            this.fetchImpl,
            customParser,
            signal
        )
    }

    async getJsonWithParams<T = any>(url: string, queryParams: Query): Promise<T> {
        const queryString = toQueryString(queryParams)
        return this.fetch(`${url}?${queryString}`, 'GET')
    }

    async patchJson(url: string, data?: Record<string, any>, headers?: Record<string, string>) {
        return this.fetch(url, 'PATCH', data, headers)
    }

    async saveSyncWithMultipleTransactions(): Promise<SaveTransactionResponse> {
        throw new Error('not implemented')
    }

    async listFragments(branchId?: string): Promise<ListFragmentsMetadataResponse> {
        return await this.getJsonWithParams(this.urls.fragments, {branchId})
    }

    async getFragmentData(fragmentTypeAndName: FragmentTypeAndName, branchId?: string): Promise<GetFragmentResponse> {
        if (!fragmentTypeAndName?.type || !fragmentTypeAndName.name) {
            throw new Error('missing type or name from fragmentTypeAndName')
        }
        return await this.getJsonWithParams(
            `${this.urls.fragments}/${fragmentTypeAndName.type}/${fragmentTypeAndName.name}`,
            {
                branchId
            }
        )
    }

    async getFragmentsData(
        fragmentTypeAndNames: FragmentTypeAndName[],
        branchId?: string
    ): Promise<GetFragmentResponse[]> {
        const promises = fragmentTypeAndNames.map(fragmentTypeAndName =>
            this.getFragmentData(fragmentTypeAndName, branchId)
        )
        const results = await Promise.allSettled(promises)
        const fulfilledResults: GetFragmentResponse[] = []
        for (const result of results) {
            if (result.status === 'fulfilled') {
                fulfilledResults.push(result.value as GetFragmentResponse)
            } else {
                const reason = result.reason as FragmentsRejectionReason
                if (reason.status === 404 && reason.details?.applicationError?.code !== 'FRAGMENT_NOT_FOUND') {
                    throw new Error(`Unexpected error: ${JSON.stringify(reason)}`)
                }
            }
        }

        return fulfilledResults
    }

    async modifySiteApps(actions: any[]): Promise<ModifySiteAppsResponse> {
        const data: any = {appsStateDelta: actions}
        return this.post(this.urls.modifySiteApps, data)
    }

    async getSchemaCollectionsByAppIds(
        appDefinitionIds: string[],
        overrideIds?: string[]
    ): Promise<SchemaCollection[]> {
        const allSchemaCollections: SchemaCollection[] = []
        let cursor: string | undefined | null
        let hasNext: boolean = true

        while (hasNext) {
            const request: GetSchemaCollectionsByAppIdsRequest = {
                appIds: appDefinitionIds,
                componentsOverrideIds: overrideIds,
                paging: cursor ? {cursor} : undefined
            }

            const response = await this.post(this.urls.schemaCollections, request)

            if (response.schemaCollections) {
                allSchemaCollections.push(...response.schemaCollections)
            }

            cursor = response.pagingMetadata?.cursors?.next
            hasNext = !!response.pagingMetadata?.hasNext
        }

        return allSchemaCollections
    }

    async batchGetVersionedSchemaCollections(versionedIds: VersionedSchemaCollectionId[]): Promise<SchemaCollection[]> {
        const chunks = _.chunk(versionedIds, VERSIONED_SCHEMA_BATCH_SIZE)
        const results = await Promise.all(
            chunks.map(chunk =>
                this.post(this.urls.versionedSchemaCollections, {
                    versionedSchemaCollectionIds: chunk
                })
            )
        )
        return _.flatMap(results, response => response?.schemaCollections ?? [])
    }

    async getComponentsByAppIds(
        appDefinitionIds: {appDefinitionId: string; version?: string}[],
        overrideIds?: string[],
        languageCode?: string
    ): Promise<TransformedAppRuntimeData[]> {
        const payload: GetAppsRuntimeDataRequest = {
            appRuntimeKeys: appDefinitionIds.map(({appDefinitionId, version}) => ({
                appId: appDefinitionId,
                ...(version && {version})
            })),
            conductionOverride: {
                overrideCriteria: {
                    platform: 'Odeditor'
                }
            },
            componentTypes: [
                ComponentType.EDITOR_REACT_COMPONENT,
                ComponentType.EDITOR_CONTEXT_PROVIDER,
                ComponentType.EDITOR_FUNCTION_LIBRARY
            ],
            fields: [RequestedFields.APP_INSTALLATION_DATA],
            componentsOverrides: {
                type: ComponentsOverridesType.IMPLICIT_OVERRIDES,
                implicitOverridesOptions: {}
            },
            ...(languageCode && {languageCode})
        }
        const url =
            overrideIds && overrideIds.length > 0
                ? `${this.urls.components}?apps-override=${overrideIds?.join(',')}`
                : this.urls.components
        const response = await this.post(url, payload)
        return mapGetComponentsResponse(response.appsRuntimeData)
    }

    async restoreRevision(revisionId: string, siteId: string, branchId?: string): Promise<any> {
        const branchIdParam = branchId ?? '00000000-0000-0000-0000-000000000000'
        return await this.post(`${this.urls.restoreRevision}/${siteId}/${revisionId}?branchId=${branchIdParam}`, {})
    }

    async fastUploadStaticFile(requestBody: FastUploadRequest): Promise<FastUploadResponse> {
        const response = await this.post<FastUploadResponse>(this.urls.fastUploadStaticFile, requestBody)
        if (!response?.name) {
            throw new Error('fastUploadStaticFile: missing name in response')
        }
        return {name: response.name}
    }

    async getStaticCssFileByName(fileName: string): Promise<string> {
        const url = getStaticCssFileRequestUrl(fileName)
        /* Added parseText custom parser to handle incorrect Content-Type header:
        service returns "application/json" but actual content is "text/css" */
        const response = await this.fetch<GetStaticCssFileByNameResponse>(url, 'GET', undefined, undefined, parseText)
        const css = response?.message
        if (!css) {
            throw new Error(`Failed to load static CSS file for ${fileName}`)
        }
        return css
    }

    async getStaticFileByName(name: string): Promise<any> {
        const response = await this.fetch(`${this.urls.staticFiles}/name/${name}`, 'GET')
        try {
            return JSON.parse(atob(response.staticFile.data))
        } catch (e) {
            throw new Error(`Failed to parse static file data for ${name}`)
        }
    }
}
